import Heap from 'heap';

class Node {
  constructor(public x: number, public y: number, public walkable: boolean = true) {}

  public opened: boolean = false;
  public closed: boolean = false;
  public parent?: Node;
  public g?: any;
  public f?: any;
  public h?: any;

  isEqual(node: Node) {
    return node && this.x === node.x && this.y === node.y;
  }
}

class Grid {
  width: number;
  height: number;
  matrix: number[][];
  nodes: Node[][];

  constructor(matrix: number[][]) {
    this.matrix = matrix;
    this.height = matrix.length;
    this.width = matrix[0].length;

    this.nodes = this.buildNodes();
  }

  public getNodeAt(x: number, y: number): Node {
    return this.nodes[y][x];
  }

  public isWalkableAt(x: number, y: number): boolean {
    return this.isInside(x, y) && this.nodes[y][x].walkable;
  }

  public isInside(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  public setWalkableAt(x: number, y: number, walkable: boolean): void {
    this.nodes[y][x].walkable = walkable;
  }

  public getNeighbors(node: Node): Node[] {
    const x = node.x;
    const y = node.y;
    const neighbors: Node[] = [];

    if (this.isWalkableAt(x, y - 1)) {
      neighbors.push(this.nodes[y - 1][x]);
    }
    if (this.isWalkableAt(x + 1, y)) {
      neighbors.push(this.nodes[y][x + 1]);
    }
    if (this.isWalkableAt(x, y + 1)) {
      neighbors.push(this.nodes[y + 1][x]);
    }
    if (this.isWalkableAt(x - 1, y)) {
      neighbors.push(this.nodes[y][x - 1]);
    }
    return neighbors;
  }

  public clone(): Grid {
    const newGrid = new Grid(this.matrix);
    const newNodes = new Array(this.height);

    for (let i = 0; i < this.height; ++i) {
      newNodes[i] = new Array(this.width);
      for (let j = 0; j < this.width; ++j) {
        newNodes[i][j] = new Node(j, i, this.nodes[i][j].walkable);
      }
    }
    newGrid.nodes = newNodes;
    return newGrid;
  }

  private buildNodes() {
    const nodes = new Array(this.height);
    for (let i = 0; i < this.height; i++) {
      nodes[i] = new Array(this.width);
      for (let j = 0; j < this.width; j++) {
        nodes[i][j] = new Node(j, i);
      }
    }

    for (let i = 0; i < this.height; ++i) {
      for (let j = 0; j < this.width; ++j) {
        if (this.matrix[i][j]) {
          nodes[i][j].walkable = false;
        }
      }
    }

    return nodes;
  }
}

function backtrace(node: Node) {
  const path = [[node.x, node.y]];
  while (node.parent) {
    node = node.parent;
    path.push([node.x, node.y]);
  }
  return path.reverse();
}

function findPath(startX: number, startY: number, endX: number, endY: number, grid: Grid) {
  const openList = new Heap((a: any, b: any) => {
    return a.f - b.f;
  });
  const startNode = grid.getNodeAt(startX, startY);
  const endNode = grid.getNodeAt(endX, endY);
  const heuristic = (dx: number, dy: number) => (dx + dy) * 100000;
  const weight = 1;
  const abs = Math.abs;
  const SQRT2 = Math.SQRT2;
  let node: Node;
  let neighbors;
  let neighbor;
  let x;
  let y;
  let ng;

  // set the `g` and `f` value of the start node to be 0
  startNode.g = 0;
  startNode.f = 0;

  // push the start node into the open list
  openList.push(startNode);
  startNode.opened = true;

  // while the open list is not empty
  while (!openList.empty()) {
    // pop the position of node which has the minimum `f` value.
    node = openList.pop();
    node.closed = true;

    // if reached the end position, construct the path and return it
    if (node.isEqual(endNode)) {
      return backtrace(endNode);
    }

    // get neigbours of the current node
    neighbors = grid.getNeighbors(node);
    for (let i = 0, l = neighbors.length; i < l; ++i) {
      neighbor = neighbors[i];

      if (neighbor.closed) {
        continue;
      }

      x = neighbor.x;
      y = neighbor.y;

      // get the distance between current node and the neighbor
      // and calculate the next g score
      ng = node.g + (x - node.x === 0 || y - node.y === 0 ? 1 : SQRT2);

      // check if the neighbor has not been inspected yet, or
      // can be reached with smaller cost from the current node
      if (!neighbor.opened || ng < neighbor.g) {
        neighbor.g = ng;
        neighbor.h = neighbor.h || weight * heuristic(abs(x - endX), abs(y - endY));
        neighbor.f = neighbor.g + neighbor.h;
        neighbor.parent = node;

        if (!neighbor.opened) {
          openList.push(neighbor);
          neighbor.opened = true;
        } else {
          // the neighbor can be reached with smaller cost.
          // Since its f value has been updated, we have to
          // update its position in the open list
          openList.updateItem(neighbor);
        }
      }
    } // end for each neighbor
  } // end while not open list empty

  // fail to find the path
  return [];
}

export { findPath, Grid, Node, backtrace };
